import { Subject, concat } from 'rxjs';
import { delay, map, scan, takeUntil, tap, toArray } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnUser } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { SnDialogService } from '@lib/ui/dialog';

import { SnUserFieldsGeneratorService } from '../../services/user-fields-generator.service';

@Component({
  templateUrl: './bulk-activate-access-request-dialog.component.html',
  styleUrls: ['./bulk-activate-access-request-dialog.component.scss'],
})
export class BulkActivateAccessRequestDialogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    this._userFieldsGenerator.getGroupsField({
      templateOptions: {
        required: true,
      },
    }),
    {
      key: 'expirationDatetime',
      type: 'datetimepicker',
      templateOptions: {
        label: 'Expiration Date',
        appearance: 'fill',
      },
    },
  ];
  public progress: number = 0;
  public isLoading: boolean = false;

  private _totalRequests = this.data.length;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnUser[],
    private readonly _dialogRef: MatDialogRef<BulkActivateAccessRequestDialogComponent>,
    private readonly _userFieldsGenerator: SnUserFieldsGeneratorService,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _dialogService: SnDialogService,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._dialogService
      .confirm({
        message: 'Are you sure you want to activate these users?',
        confirmButton: {
          text: 'CONFIRM',
          color: 'warn',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (confirm) {
          this._activateUsers();
        }
      });
  }

  private _calculateProgressPercentage(
    numbersOfCompletedRequests: number,
    numbersOfRequests: number,
  ): number {
    return (numbersOfCompletedRequests / numbersOfRequests) * 100;
  }

  private _activateUsers(): void {
    const requests = [];
    this.data.forEach((user) => {
      requests.push(
        this._usersAPI
          .activate(user.id, {
            ...this.formModel,
            email: user.email,
          })
          .pipe(delay(1000)),
      );
    });
    this.isLoading = true;
    concat(...requests)
      .pipe(
        scan((accu, curr) => accu + 1, 0),
        map((count) => this._calculateProgressPercentage(count, this._totalRequests)),
        tap((progress) => {
          this.progress = progress;
          this._cd.detectChanges();
        }),
        toArray(),
        takeUntil(this._destroy$),
      )
      .subscribe((res) => {
        this.isLoading = false;
        this.progress = 0;
        this._cd.detectChanges();
        this._dialogRef.close();
      });
  }
}
